import { useCallback, useEffect, useMemo, useState } from 'react';

export const fetchJson = async <T = any>(input: RequestInfo, init?: RequestInit): Promise<T> => {
  try {
    const res = await fetch(
      input,
      Object.assign(
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        init,
      ),
    );
    if (res.ok) {
      const data = await res.json();
      if (!data.hasOwnProperty('error')) {
        return data;
      } else {
        throw new Error(data.error.message);
      }
    } else {
      let data;
      try {
        data = await res.json();
      } catch (e) {
        //do nothing
      }
      if (data && data.hasOwnProperty('error')) {
        throw new Error(data.error.message);
      } else {
        throw new Error('Request failed with error code: ' + res.status + ' ' + res.statusText);
      }
    }
  } catch (e) {
    throw e;
  }
};

interface useFetchData<T> {
  data?: T;
  error?: Error;
  isLoading: boolean;
}

export type FetchResult<T> = { ok: true; data: T } | { ok: false; error: Error };

interface FetchState<T> extends useFetchData<T> {
  input?: RequestInfo;
  init?: RequestInit;
}

const normalizeHeaders = (headers?: HeadersInit) => {
  if (!headers) {
    return undefined;
  }
  return Object.fromEntries(new Headers(headers).entries());
};

const initKey = (init?: RequestInit) =>
  JSON.stringify({
    init: init
      ? {
          ...init,
          headers: normalizeHeaders(init.headers),
        }
      : undefined,
  });

const initFromKey = (key: string): RequestInit | undefined => JSON.parse(key).init;

export const useFetchJson = <T>(input: RequestInfo, init?: RequestInit): useFetchData<T> => {
  const stableInitKey = initKey(init);
  const stableInit = useMemo(() => initFromKey(stableInitKey), [stableInitKey]);
  const [data, setData] = useState({
    data: undefined,
    error: undefined,
    isLoading: true,
  } as FetchState<T>);

  useEffect(() => {
    let ignoreResult = false;

    fetchJson<T>(input, stableInit)
      .then((result) => {
        if (!ignoreResult) {
          setData({
            data: result,
            error: undefined,
            isLoading: false,
            input,
            init: stableInit,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        if (!ignoreResult) {
          setData({
            data: undefined,
            error,
            isLoading: false,
            input,
            init: stableInit,
          });
        }
      });

    return () => {
      ignoreResult = true;
    };
  }, [input, stableInit]);

  return data.input === input && data.init === stableInit
    ? data
    : {
        data: undefined,
        error: undefined,
        isLoading: true,
      };
};

interface QueryBase {
  [key: string]: string;
}

type Query = QueryBase | null;

export const useLazyFetchJson = <T>(
  input: RequestInfo,
  init?: RequestInit,
): {
  fetch: (input?: Query, init?: RequestInit) => Promise<FetchResult<T>>;
} & useFetchData<T> => {
  const stableInitKey = initKey(init);
  const stableInit = useMemo(() => initFromKey(stableInitKey), [stableInitKey]);
  const [data, setData] = useState({
    data: undefined,
    error: undefined,
    isLoading: false,
  } as useFetchData<T>);

  const fetchCallback = useCallback(
    (query?: Query, req?: RequestInit) => {
      setData((current) => ({
        data: current.data,
        error: current.error,
        isLoading: true,
      }));

      return fetchJson<T>(
        input + (query && Object.keys(query).length > 0 ? '?' + new URLSearchParams(query).toString() : ''),
        Object.assign({}, stableInit ?? { method: 'GET' }, req),
      )
        .then((result) => {
          setData({
            data: result,
            error: undefined,
            isLoading: false,
          });
          return { ok: true, data: result } as const;
        })
        .catch((error) => {
          console.log(error);
          setData({
            data: undefined,
            error,
            isLoading: false,
          });
          return { ok: false, error } as const;
        });
    },
    [input, stableInit],
  );

  return Object.assign(
    {
      fetch: fetchCallback,
    },
    data,
  );
};
